import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

// Validation Schema
export const allergySchema = z.object({
  allergyName: z.string().min(1, "Allergy name is required"),
  severity: z.enum(["mild", "moderate", "severe"]),
  reaction: z.string().optional(),
  notes: z.string().optional(),
});

// Types
export type AllergyForm = z.infer<typeof allergySchema>;

export interface AllergyEditMode {
  id: string;
  allergyName: string;
  severity: "mild" | "moderate" | "severe";
  reaction?: string;
  notes?: string;
}

// Static Data
export const severityOptions = [
  { value: "mild", label: "Mild" },
  { value: "moderate", label: "Moderate" },
  { value: "severe", label: "Severe" },
];

export const severityNumberToString: Record<
  number,
  "mild" | "moderate" | "severe"
> = {
  1: "mild",
  2: "moderate",
  3: "severe",
};

export const severityStringToNumber: Record<
  "mild" | "moderate" | "severe",
  number
> = {
  mild: 1,
  moderate: 2,
  severe: 3,
};

export const normalizeAllergyData = (data: any[]): AllergyEditMode[] => {
  return data.map((item) => ({
    ...item,
    severity:
      typeof item.severity === "number"
        ? (severityNumberToString[item.severity] ?? "mild")
        : (item.severity as "mild" | "moderate" | "severe"),
  }));
};

// Custom Hook for Allergy Form State
export const useAllergyFormState = (editMode?: AllergyEditMode) => {
  const [open, setOpen] = useState(false);

  const formMethods = useForm<AllergyForm>({
    resolver: zodResolver(allergySchema),
    defaultValues: editMode
      ? {
          allergyName: editMode.allergyName,
          severity:
            typeof editMode.severity === "number"
              ? severityNumberToString[editMode.severity]
              : editMode.severity,
          reaction: editMode.reaction || "",
          notes: editMode.notes || "",
        }
      : {
          severity: "mild",
        },
  });

  return {
    open,
    setOpen,
    ...formMethods,
  };
};

// ✅ When saving, convert severity back to number for backend
export const handleSubmit = (
  data: AllergyForm,
  onSave: (allergy: any) => void,
  setOpen: (open: boolean) => void,
  reset: () => void,
) => {
  const payload = {
    ...data,
    severity: severityStringToNumber[data.severity],
  };

  onSave(payload);
  setOpen(false);
  reset();
};
